import {
  isPasswordRecoverySession,
  clearPasswordRecoveryFlowIntent,
  isPasswordRecoveryFlowIntent,
} from './passwordRecoverySession';
import { isSessionLockError, isNetworkError } from './authSessionSyncHelpers';
import { getTenantMetadata } from './tenantContextLoaders';
import { resolveTenantMembershipForUser } from './resolveTenantMembershipForUser';
import { prefetchRoleHome } from '../ui/prefetchRoleHome';
import { runAfterAuthHandoff } from '../../ui/splash/authHandoffHold';
import { getPlatformAccess } from '../../services/platform/platformAccessService';
import { getMyAccessStatus } from '../../services/accessStatusService';
import { supabase } from '../../supabase';

/**
 * Factory that builds the session-sync orchestrator used by AuthProvider.
 * Extracted from AuthContext.js to keep that file under the 500-line cap;
 * this is a verbatim move of the previous inline `syncSession` closure, with
 * the surrounding `active` boolean replaced by an `isActive()` callback so
 * the function works outside the useEffect's closure scope.
 *
 * Security-critical: any change here affects every sign-in / token-refresh /
 * tab-focus path. Behavior is intentionally unchanged from the inline
 * version — only structure differs.
 *
 * Inputs (all stable references — setters from useState, refs from useRef):
 *   setters: 16 state setters that AuthProvider owns
 *   refs:    { lastSyncedUserIdRef, initialSyncDoneRef } — shared mutable
 *            state that survives across syncSession calls
 *   isActive: () => boolean — gate that blocks state updates after unmount
 *
 * Returns: async (session, syncOptions?) => void
 */
export function createSyncSession({ setters, refs, isActive }) {
  const {
    setSessionUser,
    setRecovering,
    setAuthNotice,
    setSessionIsPasswordRecovery,
    setRole,
    setTenantId,
    setTenantIsActive,
    setIndustryType,
    setCustomIndustryLabel,
    setTenantName,
    setTenantError,
    setTenantInvitePending,
    setIsSuperAdmin,
    setIsRootPlatformSuperAdmin,
    setTenantLoading,
    setLoading,
  } = setters;
  const { lastSyncedUserIdRef, initialSyncDoneRef, offlineSinceRef, freshSignInRef } = refs;

  return async function syncSession(session, syncOptions = {}) {
    const forceTenantMembershipFetch = syncOptions.forceTenantMembershipFetch === true;
    const user = session?.user ?? null;
    try {
      if (isActive()) {
        setRecovering(false);
        if (session?.user) {
          setAuthNotice(null);
        }
      }

      if (!isActive()) {
        return;
      }

      setSessionUser(user);

      if (!user) {
        lastSyncedUserIdRef.current = null;
        clearPasswordRecoveryFlowIntent();
        setSessionIsPasswordRecovery(false);
        setRole(null);
        setTenantId(null);
        setTenantIsActive(null);
        setIndustryType(null);
        setCustomIndustryLabel(null);
        setTenantName(null);
        setTenantError(null);
        setTenantInvitePending(false);
        setIsSuperAdmin(false);
        setIsRootPlatformSuperAdmin(false);
        setTenantLoading(false);
        return;
      }

      const recoveryJwt = Boolean(session && isPasswordRecoverySession(session));
      const recoveryIntent = isPasswordRecoveryFlowIntent();
      if (recoveryJwt || recoveryIntent) {
        if (!isActive()) {
          return;
        }
        setSessionIsPasswordRecovery(true);
        lastSyncedUserIdRef.current = user.id;
        initialSyncDoneRef.current = true;
        setRole(null);
        setTenantId(null);
        setTenantIsActive(null);
        setIndustryType(null);
        setCustomIndustryLabel(null);
        setTenantName(null);
        setTenantError(null);
        setTenantInvitePending(false);
        setIsSuperAdmin(false);
        setIsRootPlatformSuperAdmin(false);
        setTenantLoading(false);
        setLoading(false);
        return;
      }

      if (isActive()) {
        setSessionIsPasswordRecovery(false);
        clearPasswordRecoveryFlowIntent();
      }

      const isBackgroundResync =
        initialSyncDoneRef.current &&
        lastSyncedUserIdRef.current === user.id;

      if (!isBackgroundResync) {
        setTenantLoading(true);
      }
      setTenantError(null);
      setTenantInvitePending(false);

      let nextRole = null;
      let nextTenantId = null;
      let nextTenantIsActive = null;
      let nextIndustryType = null;
      let nextCustomIndustryLabel = null;
      let nextTenantName = null;
      let nextInvitePending = false;
      let platformAccess = null;
      try {
        // JWT fast path when hook-filled claims exist; DB path for invite/platform/stale-access resync
        const membership = await resolveTenantMembershipForUser(user, { forceTenantMembershipFetch });
        nextRole = membership.role;
        nextTenantId = membership.tenantId;
        nextInvitePending = membership.invitePending === true;
        // The role's home JS starts downloading NOW, in parallel with the
        // tenant / 2FA checks below — not after the route guards settle
        // (prefetchRoleHome.js). Fire-and-forget; UX only.
        // While Login is mid-handoff it waits until the 2FA code is VERIFIED
        // (or the password-only redirect starts) — KAN-919: nothing but the 2FA
        // decision runs before the code. Elsewhere it is immediate.
        if (nextTenantId) { const roleForHome = nextRole; runAfterAuthHandoff(() => prefetchRoleHome(roleForHome)); }

        // Run tenant metadata and platform access in parallel (both independent)
        const [tenantMeta, platformAccessResult] = await Promise.all([
          getTenantMetadata(nextTenantId),
          getPlatformAccess(user.id),
        ]);
        nextTenantIsActive = tenantMeta.isActive;
        nextIndustryType = tenantMeta.industryType;
        nextCustomIndustryLabel = tenantMeta.customIndustryLabel;
        nextTenantName = tenantMeta.tenantName;
        platformAccess = platformAccessResult;

        // P1 (2026-09-24, access-status revocation gate): the membership
        // loader above filters BOTH is_active columns (member row + tenant
        // row), so a deactivated member and a member of a deactivated
        // company resolve IDENTICALLY here — no tenant, no invite, no error
        // — and both fall back to the generic 'account' access screen even
        // when the truth is the COMPANY was turned off. Ask the DEFINER RPC
        // (reads the caller's own membership WITHOUT those filters) which
        // bucket actually applies — but only when there's a tenant story
        // left to tell: a platform user's access story is PlatformRoute's,
        // not this one. Fail-safe: getMyAccessStatus() never throws, and any
        // error / missing RPC (a separate migration lane) / unexpected shape
        // resolves to null, which changes nothing here — today's 'account'
        // screen still renders.
        if (!nextTenantId && !nextInvitePending && platformAccess?.isPlatformUser !== true) {
          const accessStatus = await getMyAccessStatus();
          if (accessStatus?.status === 'tenant_inactive') {
            // Never set tenantId here — ProtectedRoute's revocation gate
            // keys off tenantId/tenantError to decide whether app children
            // can render at all, and that gate must stay closed.
            nextTenantIsActive = false;
            nextTenantName = accessStatus.tenantName ?? nextTenantName;
          }
        }
      } catch (tenantContextError) {
        console.error('Failed to load tenant context:', tenantContextError);
        lastSyncedUserIdRef.current = null;
        if (isActive()) {
          setRole(null);
          setTenantId(null);
          setTenantIsActive(null);
          setIndustryType(null);
          setCustomIndustryLabel(null);
          setTenantName(null);
          setTenantInvitePending(false);
          setIsSuperAdmin(false);
          setIsRootPlatformSuperAdmin(false);
          setTenantError('auth.errors.tenantContextLoadFailed');
        }
        return;
      }

      if (!isActive()) {
        return;
      }

      setRole(nextRole);
      setTenantId(nextTenantId);
      setTenantIsActive(nextTenantIsActive);
      setIndustryType(nextIndustryType);
      setCustomIndustryLabel(nextCustomIndustryLabel);
      setTenantName(nextTenantName);
      setTenantInvitePending(Boolean(nextInvitePending && !nextTenantId));
      setIsSuperAdmin(
        platformAccess?.platformRole === 'super_admin' && platformAccess?.isActive === true,
      );
      setIsRootPlatformSuperAdmin(platformAccess?.isRootPlatformSuperAdmin === true);
      lastSyncedUserIdRef.current = user.id;
      initialSyncDoneRef.current = true;

      // RD-SESS-002 (role-gated single active session). Deferred here from the
      // AuthContext SIGNED_IN handler because the role is not known synchronously
      // at sign-in. Workers share field devices, so a fresh worker sign-in revokes
      // other devices' refresh tokens server-side. Admin/supervisor/billing get
      // concurrent sessions (multi-device is normal for office/owner roles) and are
      // never revoked — this is what lets an admin open an invoice preview on one
      // device while signed in on another without being signed out.
      // freshSignInRef is set true only on a genuine fresh credential sign-in
      // (excluding invite/reset flows). Consume it regardless of role so a later
      // TOKEN_REFRESHED can't re-trigger; fail open — never revoke when role is
      // anything other than 'worker' (including unknown).
      if (freshSignInRef?.current) {
        freshSignInRef.current = false;
        if (nextRole === 'worker') {
          void (async () => {
            try {
              const { error } = await supabase.auth.signOut({ scope: 'others' });
              if (error) console.warn('RD-SESS-002 signOut(others):', error.message);
              // Realtime kick for other devices (Account Sessions signal path).
              // Best-effort — GoTrue prune already revoked; gate covers REST.
              const { error: signalErr } = await supabase.rpc('signal_other_own_sessions_revoked');
              if (signalErr) console.warn('RD-SESS-002 signal others:', signalErr.message);
            } catch (e) {
              console.warn('RD-SESS-002 signOut(others):', e);
            }
          })();
        }
      }
    } catch (err) {
      if (isNetworkError(err)) {
        // Covers both reconnection (preserve state) and cold-start offline (PWA reopened without network).
        console.warn(lastSyncedUserIdRef.current ? 'Network error during sync; preserving auth state.' : 'Offline on initial load; waiting for connection.');
        if (isActive()) {
          offlineSinceRef.current = offlineSinceRef.current ?? Date.now();
          setAuthNotice({ type: 'info', key: 'auth.notices.connectionLost' });
          setLoading(false);
          setTenantLoading(false);
        }
        return;
      }
      if (isSessionLockError(err)) {
        console.warn('Transient auth lock detected during session sync; ignoring and waiting for next auth event.');
        if (isActive()) {
          if (lastSyncedUserIdRef.current === user?.id) {
            setAuthNotice({
              type: 'info',
              key: 'auth.notices.sessionRefreshing',
            });
          } else {
            setRecovering(true);
            setAuthNotice({
              type: 'info',
              key: 'auth.notices.sessionRestoring',
            });
          }
        }
        return;
      }

      console.error('Auth init error:', err);
      if (isActive()) {
        lastSyncedUserIdRef.current = null;
        initialSyncDoneRef.current = false;
        setRole(null);
        setTenantId(null);
        setTenantIsActive(null);
        setIndustryType(null);
        setCustomIndustryLabel(null);
        setTenantName(null);
        setTenantInvitePending(false);
        setIsSuperAdmin(false);
        setIsRootPlatformSuperAdmin(false);
        setTenantError('auth.errors.tenantMembershipLoadFailed');
        setRecovering(false);
        setAuthNotice({
          type: 'error',
          key: 'auth.notices.recoveryFailed',
        });
      }
    } finally {
      if (isActive()) {
        setTenantLoading(false);
        setLoading(false);
      }
    }
  };
}
